/**
 * Routes for the side activities, built from the road graph: a race's driving line (the waypoints the
 * rivals follow, with corner speeds), its checkpoints, the start grid, and kerbside stops for the minicab.
 * Pure functions of the layout; nothing here adds a collider, a node or a POI (the server imports the layout).
 */
import { angleDelta, distance, type V2 } from '../core/math';
import { NODES } from '../world/lowBorough/data';
import { nearestEdge } from '../world/roads';
import type { CityLayout, RoadEdge } from '../world/types';
import { drivePoint, junctionCap, type Waypoint } from '../missions/autopilot';

/** A route through named junctions (`world/lowBorough/data.ts` node keys), starting and ending mid-street. */
export interface RouteSpec {
  /** Node keys in driving order; the first leg starts at `startT` along it, the last ends at `endT`. */
  nodes: readonly string[];
  startT: number;
  endT: number;
}

export interface Checkpoint {
  pos: V2;
  radius: number;
  /** Street the checkpoint is on ("Coalgate"), for the objective line. */
  street: string;
}

export interface RaceLine {
  /** Rivals' driving line (left-hand lane, alley centre lines), with corner speed caps. */
  points: Waypoint[];
  checkpoints: Checkpoint[];
  /** Grid: where the start line is and which way it faces. */
  start: V2;
  heading: number;
  /** Metres from the start to the finish along the line. */
  length: number;
  /**
   * Index of the finish in `points`. The points after it are the run-off: on past the flag and in to the
   * kerb, so a rival that has finished doesn't stop dead on the line in front of everyone else.
   */
  finishIndex: number;
}

/** Layout node id for a data key, checked by position (a stub layout simply has no such node). */
export function nodeId(layout: CityLayout, key: string): number | null {
  const k = NODES.findIndex((n) => n[0] === key);
  if (k < 0) return null;
  const want = NODES[k]!;
  const n = layout.roads.nodes[k];
  if (n && Math.abs(n.pos.x - want[1]) < 0.5 && Math.abs(n.pos.z - want[2]) < 0.5) return k;
  const hit = layout.roads.nodes.find((q) => Math.abs(q.pos.x - want[1]) < 0.5 && Math.abs(q.pos.z - want[2]) < 0.5);
  return hit ? hit.id : null;
}

/** The edge from node a to node b and whether it is driven forward; null if there is none (or it's one-way against us). */
export function legEdge(layout: CityLayout, a: number, b: number): { edge: RoadEdge; forward: boolean } | null {
  for (const e of layout.roads.edges) {
    if (e.from === a && e.to === b && e.lanesForward > 0) return { edge: e, forward: true };
    if (e.to === a && e.from === b && !e.oneWay && e.lanesBackward > 0) return { edge: e, forward: false };
  }
  return null;
}

/** Build the race line for `spec`, or null if the layout doesn't have those streets (stub layouts). */
export function raceLine(layout: CityLayout, spec: RouteSpec): RaceLine | null {
  const ids: number[] = [];
  for (const k of spec.nodes) {
    const id = nodeId(layout, k);
    if (id === null) return null;
    ids.push(id);
  }
  const legs: { edge: RoadEdge; forward: boolean }[] = [];
  for (let i = 0; i < ids.length - 1; i++) {
    const leg = legEdge(layout, ids[i]!, ids[i + 1]!);
    if (!leg) return null;
    legs.push(leg);
  }
  if (!legs.length) return null;
  const nodes = layout.roads.nodes;
  const points: Waypoint[] = [];
  const checkpoints: Checkpoint[] = [];
  const cpRadius = (e: RoadEdge): number => Math.max(8, e.width / 2 + 3);
  let sinceCp = 0;
  let last: V2 | null = null;
  let length = 0;
  const push = (p: Waypoint): void => {
    if (last) {
      const d = distance(last, p);
      length += d;
      sinceCp += d;
    }
    last = p;
    points.push(p);
  };
  for (let k = 0; k < legs.length; k++) {
    const { edge: e, forward: fwd } = legs[k]!;
    const len = distance(nodes[e.from]!.pos, nodes[e.to]!.pos);
    const t0 = k === 0 ? spec.startT : 0;
    const t1 = k === legs.length - 1 ? spec.endT : 1;
    if (k > 0) {
      // Through the junction: a speed for the turn, and a checkpoint if the route turns here.
      const prev = legs[k - 1]!;
      const pa = nodes[prev.forward ? prev.edge.from : prev.edge.to]!.pos;
      const node = nodes[fwd ? e.from : e.to]!.pos;
      const nb = nodes[fwd ? e.to : e.from]!.pos;
      const turn = Math.abs(angleDelta(Math.atan2(node.x - pa.x, node.z - pa.z), Math.atan2(nb.x - node.x, nb.z - node.z)));
      const narrow = e.alley || prev.edge.alley;
      push({ x: node.x, z: node.z, cap: narrow ? Math.min(turn > 0.35 ? 4.5 : 7, junctionCap(turn)) : junctionCap(turn) });
      if (turn > 0.35) {
        const ix = layout.intersections.find((q) => q.nodeId === (fwd ? e.from : e.to));
        checkpoints.push({ pos: { x: node.x, z: node.z }, radius: Math.max(9, (ix?.radius ?? 6) + 4), street: e.name });
        sinceCp = 0;
      }
    }
    const steps = Math.max(2, Math.ceil((len * (t1 - t0)) / 12));
    for (let i = k === 0 ? 0 : 1; i <= steps; i++) {
      const t = t0 + ((t1 - t0) * i) / steps;
      if (k < legs.length - 1 && i === steps) break; // the next junction point stands in for t = 1
      const p: Waypoint = drivePoint(layout, e.id, fwd, t);
      if (e.alley) p.cap = 7;
      push(p);
      // Long straights get a gate every ~170 m so nobody cuts the course through the back streets.
      if (sinceCp > 170 && !(k === legs.length - 1 && i === steps)) {
        checkpoints.push({ pos: { x: p.x, z: p.z }, radius: cpRadius(e), street: e.name });
        sinceCp = 0;
      }
    }
  }
  const lastLeg = legs[legs.length - 1]!;
  const end = points[points.length - 1]!;
  checkpoints.push({ pos: { x: end.x, z: end.z }, radius: cpRadius(lastLeg.edge), street: lastLeg.edge.name });
  const finishIndex = points.length - 1;
  // Run-off: on along the last street, easing over to the kerb, slowing to a stop.
  {
    const e = lastLeg.edge;
    const fwd = lastLeg.forward;
    const pa = nodes[fwd ? e.from : e.to]!.pos;
    const pb = nodes[fwd ? e.to : e.from]!.pos;
    const len = distance(pa, pb) || 1;
    const left = { x: (pb.z - pa.z) / len, z: -(pb.x - pa.x) / len };
    const toKerb = e.alley ? 0 : e.laneWidth * 0.5 + 0.9;
    const t0 = spec.endT;
    const t1 = Math.min(0.92, t0 + 70 / len);
    const n = Math.max(2, Math.ceil(((t1 - t0) * len) / 12));
    for (let i = 1; i <= n; i++) {
      const t = t0 + ((t1 - t0) * i) / n;
      const p = drivePoint(layout, e.id, fwd, t);
      const k = (toKerb * i) / n;
      points.push({ x: p.x + left.x * k, z: p.z + left.z * k, cap: Math.max(2, 14 * (1 - i / n)) });
    }
  }
  const a = points[0]!;
  const b = points[Math.min(2, points.length - 1)]!;
  return { points, checkpoints, start: { x: a.x, z: a.z }, heading: Math.atan2(b.x - a.x, b.z - a.z), length, finishIndex };
}

/**
 * Grid slots behind and beside the start line (the player has pole: `start`). Slot 0 alongside on the
 * crown of the road, then two rows back. Heading follows the convention: forward = (sin h, cos h).
 */
export function gridSlots(start: V2, heading: number, n: number): V2[] {
  const f = { x: Math.sin(heading), z: Math.cos(heading) };
  const right = { x: -Math.cos(heading), z: Math.sin(heading) };
  const offsets: [number, number][] = [
    [0, -3.6],
    [-8, 0],
    [-8, -3.6],
    [-16, 0],
    [-16, -3.6],
  ];
  const out: V2[] = [];
  for (let i = 0; i < n; i++) {
    const [back, side] = offsets[i % offsets.length]!;
    // `side` negative = to the right of travel (the crown on a British road).
    out.push({ x: start.x + f.x * back - right.x * side, z: start.z + f.z * back - right.z * side });
  }
  return out;
}

/**
 * Where a car pulls in for a door: the road beside it, in the near lane by its outer edge — not in the
 * kerbside parking strip, where parked cars sit.
 */
export function kerbStop(layout: CityLayout, door: V2): V2 {
  const ne = nearestEdge(layout, door);
  if (!ne) return door;
  const a = layout.roads.nodes[ne.edge.from]!.pos;
  const b = layout.roads.nodes[ne.edge.to]!.pos;
  const c = { x: a.x + (b.x - a.x) * ne.t, z: a.z + (b.z - a.z) * ne.t };
  const dx = door.x - c.x;
  const dz = door.z - c.z;
  const d = Math.hypot(dx, dz);
  if (d < 0.5) return c;
  const lanes = (ne.edge.lanesForward + ne.edge.lanesBackward) * ne.edge.laneWidth;
  const off = Math.min(d, Math.max(0, lanes / 2 - ne.edge.laneWidth * 0.4));
  return { x: c.x + (dx / d) * off, z: c.z + (dz / d) * off };
}

/** Road distance estimate between two points (sum of A* legs would be nicer; the planner is in autopilot). */
export function routeLength(points: readonly V2[]): number {
  let s = 0;
  for (let i = 1; i < points.length; i++) s += distance(points[i - 1]!, points[i]!);
  return s;
}
